import { SupabaseClient } from "@supabase/supabase-js";

const VERIFY_CACHE = "push-subscription-verify";
const MIN_VERIFY_INTERVAL = 60 * 1000;//24 * 60 * 60 * 1000; // 24 hours

async function getLastVerified(): Promise<number> {
    const cache = await caches.open(VERIFY_CACHE);
    const res = await cache.match("last-verified");
    return res ? parseInt(await res.text()) : 0;
}

async function setLastVerified() {
    const cache = await caches.open(VERIFY_CACHE);
    await cache.put("last-verified", new Response(Date.now().toString()));
}

export async function verifyNotificationSubscription(swRegistration: ServiceWorkerRegistration, supabase: SupabaseClient, force: boolean = false) {
    if (!force && Date.now() - await getLastVerified() < MIN_VERIFY_INTERVAL) return true;
    await setLastVerified();

    const notificationSubscription = await swRegistration.pushManager.getSubscription();
    if (!notificationSubscription) {
        return false;
    }

    const { data: isValid, error } = await supabase.rpc("verify_notification_subscription", { p_endpoint: notificationSubscription.endpoint });
    if (error) {
        console.error("error verifying push subscription", error);
        return false;
    }

    if (!isValid) {
        await notificationSubscription.unsubscribe();
        return false;
    }

    return true;
}

function urlBase64ToUint8Array(base64String: string) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = atob(base64);
    return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

let enablePushInProgress = false;
export async function enablePush(supabase: SupabaseClient) {
    if (enablePushInProgress) return;

    try {
        enablePushInProgress = true;

        const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
        if (!vapidPublicKey) {
            console.error("env var VITE_VAPID_PUBLIC_KEY is missing");
            return null;
        };

        const perm = Notification.permission === "granted"
            ? "granted"
            : await Notification.requestPermission();

        if (perm !== "granted") {
            return null;
        }

        const reg = await navigator.serviceWorker.ready;

        const sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });

        const { error } = await supabase.functions.invoke("push-subscribe", {
            method: "POST",
            body: sub.toJSON(),
        });

        if (error) {
            await sub.unsubscribe();
            console.error("failed to subscribe to notifications", error);
            return null;
        }

        return sub;
    } finally {
        enablePushInProgress = false;
    }
}